import {
  BoundSelectField,
  BoundTextField,
  Button,
  Css,
  Icon,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Palette,
  Tag,
  TagType,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { Maybe } from "graphql/jsutils/Maybe";
import { Observer } from "mobx-react";
import { useState } from "react";
import { useHistory } from "react-router-dom";
import { createDesignPackageUrl } from "src/RouteUrls";
import { AggregateVersionStatus } from "src/generated/graphqlTypes";
import {
  CopyDesignPackage_ReadyPlanLikeFragment,
  useCopyDesignPackage_CopyDesignPackageMutation,
  useCopyDesignPackage_ReadyPlanLikeQuery,
} from "src/routes/libraries/design-catalog/CopyDesignPackageModal.generated";
import { queryResult } from "src/utils";

type CopyDesignPackageModalProps = { id: string };

/** Copies a design package and navigates to the edit page after successful creation */
export function CopyDesignPackageModal({ id }: CopyDesignPackageModalProps) {
  const packageQuery = useCopyDesignPackage_ReadyPlanLikeQuery({ variables: { id } });
  return queryResult(packageQuery, (data) => <CopyDesignPackageModalDataView designPackage={data.readyPlanLike2} />);
}

function CopyDesignPackageModalDataView({ designPackage }: { designPackage: CopyDesignPackage_ReadyPlanLikeFragment }) {
  const { id, name, version, aggregateVersions } = designPackage;
  const { closeModal } = useModal();
  const history = useHistory();
  const [createCopyMutation] = useCopyDesignPackage_CopyDesignPackageMutation();
  const [copyMutationError, setCopyMutationError] = useState<string>();

  const copyPackageForm = useFormState({
    config: copyDesignConfig,
    init: {
      input: { id, name, version },
      onlyOnce: true,
      map: ({ id, name, version }) => {
        return { sourcePackageId: id, sourcePackageVersionId: version.id, name: "(Copy) " + name };
      },
    },
  });

  async function handleCreateCopy() {
    setCopyMutationError(undefined);
    const { sourcePackageId, sourcePackageVersionId, name } = copyPackageForm.value;
    const { data } = await createCopyMutation({
      variables: { input: { sourcePackageId, sourcePackageVersionId, name: name! } },
    });
    const { designPackage } = data?.copyDesignPackage || {};

    if (designPackage) {
      history.push(createDesignPackageUrl(designPackage.id, designPackage.version.id));
      closeModal();
    }
  }

  return (
    <>
      <ModalHeader>Copy Design Package</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.w100.bb.my2.pb2.gap2.bcGray700.$}>
          <div css={Css.df.fdc.gap1.$}>
            <div css={Css.dg.gtc("1fr 2fr").aic.gap1.$}>
              <div css={Css.gray700.$}>Design Package</div>
              <div css={Css.md.$}>{name}</div>
              <div css={Css.gray700.$}>Version</div>
              <div css={Css.md.$}>
                <BoundSelectField
                  field={copyPackageForm.sourcePackageVersionId}
                  label="Version"
                  labelStyle="hidden"
                  options={aggregateVersions}
                  getOptionLabel={(val) =>
                    `${val.status.code === AggregateVersionStatus.Draft ? "Draft of " : ""}v${val.version?.toString()}`
                  }
                  getOptionMenuLabel={(val) => (
                    <div css={Css.df.gap1.$}>
                      <Tag text={val.status.name} type={rpavStatusTag[val.status.code] || "neutral"} />
                      {`${val.status.code === AggregateVersionStatus.Draft ? "Draft of " : ""}v${val.version?.toString()}`}
                    </div>
                  )}
                  getOptionValue={(val) => val.id}
                />
              </div>
            </div>
          </div>
        </div>

        <div css={Css.df.fdc.gap1.$}>
          <div css={Css.dg.gtc("1fr 2fr").aic.gap1.$}>
            <div css={Css.gray700.$}>New Design Package Name</div>
            <BoundTextField hideErrorMessage labelStyle="hidden" fullWidth field={copyPackageForm.name} label="Name" />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        {copyMutationError && (
          <div data-testid="copyMutationError" css={Css.red600.df.aic.$}>
            <Icon icon="error" inc={3} color={Palette.Red600} />
            <span css={Css.ml1.$}>{copyMutationError}</span>
          </div>
        )}
        <Button label="Cancel" variant="quaternary" onClick={closeModal} />
        <Observer>
          {() => <Button label="Create Copy" disabled={!copyPackageForm.valid} onClick={handleCreateCopy} />}
        </Observer>
      </ModalFooter>
    </>
  );
}

type CreateCopyFormValue = { sourcePackageId: string; sourcePackageVersionId: Maybe<string>; name: Maybe<string> };

const copyDesignConfig: ObjectConfig<CreateCopyFormValue> = {
  sourcePackageId: { type: "value", readOnly: true },
  sourcePackageVersionId: { type: "value", rules: [required] },
  name: { type: "value", rules: [required] },
};

const rpavStatusTag: Record<AggregateVersionStatus, TagType> = {
  [AggregateVersionStatus.Draft]: "info",
  [AggregateVersionStatus.Active]: "success",
  [AggregateVersionStatus.Archived]: "caution",
};
